/**
 * useCollabInfra — one-stop wiring for the real-time collaboration stack.
 *
 * iter-268 — extracted from ELPMStudioDAW.jsx to shrink the god-component
 * (was ~2484 lines). Bundles together the four Phase-1..3 hooks that
 * previously all lived side-by-side in the DAW body:
 *
 *   1. useCollab           — WebSocket + peer roster
 *   2. useWebRTCMesh       — peer-to-peer data + binary channels
 *   3. useYjsRoom          — CRDT-backed mixer/transport/fx state
 *   4. useChannelLocks     — per-channel exclusive control
 *
 * Also owns:
 *   - collabRoomId / collabUserName state (URL-synced + localStorage-persisted)
 *   - The engine→collab broadcast bridge (mixer/transport/fx/sidechain events)
 *   - peerCursors derived from Yjs awareness
 *   - Local awareness broadcast of our selectedChannel + color
 *
 * Inputs:
 *   engine, selectedChannel, setPlaying — plumbed through so the bridge can
 *   route inbound Yjs updates to the local audio engine and toggle transport.
 *
 * Returns everything the DAW body + right-rail need to render the collab UI:
 *   { collab, mesh, yjs, channelLocks, peerCursors,
 *     collabRoomId, setCollabRoomId, collabUserName, setCollabUserName }
 */
import { useEffect, useRef, useState } from "react";
import * as Y from "yjs";

import useCollab from "@/lib/useCollab";
import useWebRTCMesh from "@/lib/useWebRTCMesh";
import useYjsRoom from "@/lib/useYjsRoom";
import useYjsEngineBridge from "@/lib/useYjsEngineBridge";
import useChannelLocks from "@/lib/useChannelLocks";
import { peerColor } from "@/components/CollabModal";

export default function useCollabInfra({ engine, selectedChannel, setPlaying }) {
  // roomId & userName drive the WebSocket connection.
  // URL param `?collab=XYZ` auto-joins so shared invite links work.
  const [collabRoomId, setCollabRoomId] = useState(() => {
    if (typeof window === "undefined") return null;
    const sp = new URLSearchParams(window.location.search);
    return sp.get("collab") || null;
  });
  const [collabUserName, setCollabUserName] = useState(() => {
    try { return localStorage.getItem("elpm-collab-name") || ""; } catch { return ""; }
  });

  // Map of peerId → {name, channel, ts} — render colored cursor badges on the
  // mixer at each peer's currently-selected channel. Derived from awareness.
  const [peerCursors, setPeerCursors] = useState({});

  // Push the room id into the URL so refreshes keep the user in the room.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (collabRoomId) url.searchParams.set("collab", collabRoomId);
    else url.searchParams.delete("collab");
    window.history.replaceState({}, "", url.toString());
  }, [collabRoomId]);

  // Inbound peer event handler — legacy-message safety net. Every well-known
  // channel (mixer, fx, sidechain, transport, cursor) is now driven through
  // the Y.Doc / Awareness path; this dispatcher exists only so a downgraded
  // client that hasn't upgraded past Phase-1 can still interoperate.
  const applyCollabEvent = useRef(() => {});
  applyCollabEvent.current = (msg) => {
    if (!msg || !msg.type) return;
    if (msg.type === "welcome" || msg.type === "peer_join" || msg.type === "peer_leave") return;
    const p = msg.payload || {};
    engine._collabSilent = true;
    try {
      if (msg.type === "mixer" && typeof p.channel === "number") {
        if (p.field === "volume" && typeof p.value === "number") engine.setChannelVolume(p.channel, p.value);
        else if (p.field === "pan" && typeof p.value === "number") engine.setChannelPan(p.channel, p.value);
        else if (p.field === "muted") engine.setChannelMute(p.channel, !!p.value);
        else if (p.field === "solo") engine.setChannelSolo(p.channel, !!p.value);
      } else if (msg.type === "transport") {
        if (p.action === "play") { engine.seekTo(p.position || 0); engine.play(); setPlaying(true); }
        else if (p.action === "pause") { engine.pause(); setPlaying(false); }
        else if (p.action === "stop") { engine.stop(); setPlaying(false); }
        else if (p.action === "seek" && typeof p.position === "number") engine.seekTo(p.position);
      } else if (msg.type === "sidechain" && typeof p.channel === "number") {
        const patch = {};
        if ("mode" in p) patch.mode = p.mode;
        if ("keyChannel" in p) patch.keyChannel = p.keyChannel;
        if (typeof p.threshold === "number") patch.threshold = p.threshold;
        if (typeof p.depth === "number") patch.depth = p.depth;
        if (typeof p.attack === "number") patch.attack = p.attack;
        if (typeof p.release === "number") patch.release = p.release;
        if (typeof p.lfoSubdivision === "number") patch.lfoSubdivision = p.lfoSubdivision;
        engine.setSidechain(p.channel, patch);
      } else if (msg.type === "fx_param") {
        if (typeof p.channel === "number" && p.effect && p.param && typeof p.value === "number") {
          engine.setChannelEffectParam(p.channel, p.effect, p.param, p.value);
        }
      }
      // NOTE: `cursor` events from legacy clients are dropped — awareness owns
      // that data now. Drawing two cursor sources concurrently risked
      // double-render flicker for the transition period.
    } finally {
      engine._collabSilent = false;
    }
  };

  const meshSignalRef = useRef(null);
  const yjsSignalRef = useRef(null);
  const yjsApplyBinaryRef = useRef(null);

  const collab = useCollab({
    roomId: collabRoomId,
    name: collabUserName || "Anonymous",
    onMessage: (m) => {
      // Route WebRTC signaling (rtc:offer/answer/ice) to the mesh hook.
      if (m?.type && typeof m.type === "string" && m.type.startsWith("rtc:")) {
        meshSignalRef.current?.(m);
        return;
      }
      // Route Yjs CRDT envelopes (and awareness presence) to the doc sync hook.
      if (m?.type && typeof m.type === "string"
          && (m.type.startsWith("crdt:") || m.type.startsWith("awareness:"))) {
        yjsSignalRef.current?.(m);
        return;
      }
      applyCollabEvent.current(m);
      // Prune the peer's cursor when they leave so stale badges don't linger.
      if (m?.type === "peer_leave" && m.user?.id) {
        setPeerCursors((prev) => {
          const next = { ...prev };
          delete next[m.user.id];
          return next;
        });
      }
    },
  });

  // ─── WebRTC peer-mesh (Phase-1) ───
  // Layered on top of collab's WebSocket which we re-use as the signaling
  // channel. Provides live mic monitoring + low-latency data channels.
  // The `onPeerBinary` callback is the inbound path for Phase-2.5 — every
  // ArrayBuffer arriving on a data channel is treated as a Yjs CRDT update
  // and applied to the local doc via `yjsApplyBinaryRef`.
  const mesh = useWebRTCMesh({
    me: collab.me,
    peers: collab.peers,
    send: collab.send,
    enabled: collab.status === "connected",
    maxPeers: 4,
    onPeerData: undefined,
    onPeerBinary: (peerId, bytes) => {
      yjsApplyBinaryRef.current?.(bytes);
    },
  });
  useEffect(() => { meshSignalRef.current = mesh.handleSignal; }, [mesh.handleSignal]);

  // ─── Yjs CRDT (Phase-2 + 2.5) ───
  // Shared mixer + transport state via a Y.Doc, synced over the same
  // /api/collab WebSocket. Phase-2.5 also routes updates over the WebRTC
  // data channels for sub-30ms peer-to-peer delivery.
  const yjs = useYjsRoom({
    me: collab.me,
    peers: collab.peers,
    send: collab.send,
    enabled: collab.status === "connected",
    mesh,
  });
  useEffect(() => { yjsSignalRef.current = yjs.handleSignal; }, [yjs.handleSignal]);
  useEffect(() => { yjsApplyBinaryRef.current = yjs.applyRemoteBinary; }, [yjs.applyRemoteBinary]);

  // ─── Channel locks (Phase-3) ───
  // Per-channel exclusive control written into Y.Map("channels").<idx>.lockedBy.
  // Hook auto-evicts locks whose holder dropped off awareness.
  const channelLocks = useChannelLocks({
    doc: yjs.doc,
    awareness: yjs.awareness,
    awarenessStates: yjs.awarenessStates,
    ready: yjs.ready && collab.status === "connected",
  });

  // Best-effort: release our locks when the user leaves the room.
  useEffect(() => {
    if (collab.status === "connected") return undefined;
    return () => { channelLocks.releaseAllMine(); };
  }, [collab.status, channelLocks]);

  // Bridge remote Y.Doc changes → audio engine. Local mutations are tagged
  // with LOCAL_ORIGIN by useYjsRoom.transact so this observer ignores them.
  useYjsEngineBridge({
    doc: yjs.doc,
    ready: yjs.ready && collab.status === "connected",
    engine,
    setPlaying,
  });

  // Broadcast our current channel selection via the Yjs Awareness layer.
  // Awareness has built-in dead-peer eviction (outliveTimeout), so when a
  // collaborator disconnects abruptly their cursor disappears automatically.
  //
  // NOTE: deps list intentionally uses `yjs.setLocalAwareness` (useCallback-
  // stable) NOT `yjs` (fresh object identity every render). Listing `yjs`
  // here would re-fire on every render and, combined with Awareness change
  // → setAwarenessStates → re-render, creates an infinite loop.
  useEffect(() => {
    if (collab.status !== "connected") return;
    yjs.setLocalAwareness({
      cursor: { channel: selectedChannel },
      name: collabUserName || "Anonymous",
      color: peerColor(String(yjs.awareness.clientID)),
    });
    // We intentionally depend on the STABLE fields we actually read
    // (setLocalAwareness + awareness) instead of `yjs` itself — the hook
    // returns a fresh object identity every render, and listing `yjs`
    // would re-fire on every render and infinite-loop with the
    // awareness → setAwarenessStates → render cycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannel, collab.status, collabUserName, yjs.setLocalAwareness, yjs.awareness]);

  // Derive `peerCursors` from awareness — replaces the legacy `peer_cursors`
  // state that was updated by the WS cursor handler. Same shape.
  //
  // Deps: `yjs.awarenessStates` is the only reactive dep we need — the
  // awareness ref itself is stable per Y.Doc lifetime.
  useEffect(() => {
    if (collab.status !== "connected") {
      setPeerCursors({});
      return;
    }
    const next = {};
    const localClientId = yjs.awareness.clientID;
    yjs.awarenessStates.forEach((state, clientId) => {
      if (clientId === localClientId) return;
      if (!state?.cursor || typeof state.cursor.channel !== "number") return;
      next[String(clientId)] = {
        name: state.name || "Anonymous",
        channel: state.cursor.channel,
        ts: Date.now(),
      };
    });
    setPeerCursors(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yjs.awarenessStates, collab.status]);

  // Install the engine→collab broadcast bridge whenever connected.
  // Phase-2: mixer + transport events go into the shared Y.Doc so changes
  // are conflict-free under concurrent edits. Everything else (fx_param,
  // sidechain, cursor) still rides the legacy WS path.
  useEffect(() => {
    if (collab.status !== "connected") {
      engine.onCollabEvent = null;
      return () => { engine.onCollabEvent = null; };
    }
    engine.onCollabEvent = (evt) => {
      const p = evt?.payload || {};
      if (evt?.type === "mixer" && typeof p.channel === "number" && p.field) {
        yjs.transact(() => {
          const channels = yjs.doc.getMap("channels");
          const key = String(p.channel);
          let chMap = channels.get(key);
          if (!(chMap instanceof Y.Map)) {
            chMap = new Y.Map();
            channels.set(key, chMap);
          }
          chMap.set(p.field, p.value);
        });
        return;
      }
      if (evt?.type === "transport" && p.action) {
        yjs.transact(() => {
          const t = yjs.doc.getMap("transport");
          t.set("action", p.action);
          if (typeof p.position === "number") t.set("position", p.position);
          t.set("ts", Date.now());
        });
        return;
      }
      if (evt?.type === "fx_param"
          && typeof p.channel === "number"
          && p.effect && p.param
          && typeof p.value === "number") {
        // Flat-key Y.Map — chosen because FX knob drags emit dozens of events
        // per second and a single flat map is cheaper to observe than nested.
        yjs.transact(() => {
          yjs.doc.getMap("fx").set(`${p.channel}:${p.effect}:${p.param}`, p.value);
        });
        return;
      }
      if (evt?.type === "sidechain" && typeof p.channel === "number") {
        yjs.transact(() => {
          const sidechain = yjs.doc.getMap("sidechain");
          const key = String(p.channel);
          let sMap = sidechain.get(key);
          if (!(sMap instanceof Y.Map)) {
            sMap = new Y.Map();
            sidechain.set(key, sMap);
          }
          ["mode", "keyChannel", "threshold", "depth", "attack", "release", "lfoSubdivision"]
            .forEach((f) => { if (f in p) sMap.set(f, p[f]); });
        });
        return;
      }
      // Fall through to legacy WS path for anything we haven't migrated yet.
      collab.send(evt.type, evt.payload);
    };
    return () => { engine.onCollabEvent = null; };
    // Same rationale as the awareness effect above: `yjs` gets a fresh
    // object identity every render, so we depend on the STABLE
    // sub-references we actually call (yjs.transact, yjs.doc). Listing
    // `collab` and `yjs` themselves would re-fire on every render and
    // rip up the engine.onCollabEvent hook constantly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collab.status, collab.send, engine, yjs.transact, yjs.doc]);

  return {
    collab,
    mesh,
    yjs,
    channelLocks,
    peerCursors,
    collabRoomId,
    setCollabRoomId,
    collabUserName,
    setCollabUserName,
  };
}
